<!--
  - SPDX-FileCopyrightText: 2020 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<div
		id="call-container"
		@mousemove="debounceHandleMovement"
		@keydown="debounceHandleMovement"
		@mouseleave="hideOverlay">
		<ViewerOverlayCallView
			v-if="isViewerOverlay"
			:token="token"
			:model="promotedParticipantModel"
			:sharedData="promotedParticipantModel && sharedDatas[promotedParticipantModel.attributes.peerId]"
			:screens="screens"
			:localSharedData="localSharedData" />

		<template v-else>
			<EmptyCallView v-if="showEmptyCallView" :isSidebar="isSidebar" />

			<div id="videos" :class="{ 'is-sidebar': isSidebar }">
				<div
					v-if="showPromotedArea"
					class="video__promoted"
					:class="{ 'full-page': showFullPage }">
					<!-- Selected video override mode -->
					<VideoVue
						v-if="showSelectedVideo && selectedCallParticipantModel"
						:key="`promoted-${selectedVideoPeerId}`"
						:data-tile-session-id="selectedCallParticipantModel.attributes.nextcloudSessionId"
						:token="token"
						:model="selectedCallParticipantModel"
						:sharedData="sharedDatas[selectedVideoPeerId]"
						:showTalkingHighlight="false"
						:isFullPage="showFullPage"
						isGrid
						isBig
						fitVideo />

					<!-- Local Video Override mode (following own video) -->
					<LocalVideo
						v-else-if="showLocalVideo"
						ref="localVideo"
						:token="token"
						:localMediaModel="localMediaModel"
						:localCallParticipantModel="localCallParticipantModel"
						:isStripe="false"
						:isSidebar="false"
						isBig
						fitVideo />

					<!-- Screens -->
					<!-- Local screen -->
					<ScreenShare
						v-else-if="showLocalScreen"
						key="screen-local"
						:token="token"
						:localMediaModel="localMediaModel"
						:showVideoOverlay="showVideoOverlay"
						:sharedData="localSharedData"
						isBig />
					<!-- Remote or selected screen -->
					<ScreenShare
						v-else-if="(showRemoteScreen || showSelectedScreen) && shownRemoteScreenCallParticipantModel"
						:key="`screen-${shownRemoteScreenPeerId}`"
						:token="token"
						:callParticipantModel="shownRemoteScreenCallParticipantModel"
						:showVideoOverlay="showVideoOverlay"
						:sharedData="sharedDatas[shownRemoteScreenPeerId]"
						isBig />
					<!-- Several active speakers sharing the main area -->
					<SpeakersGrid
						v-else-if="showSpeakersGrid"
						:token="token"
						:models="promotedSpeakerModels"
						:sharedDatas="sharedDatas"
						:showVideoOverlay="showVideoOverlay"
						@selectVideo="handleSelectVideo" />
					<!-- Promoted "autopilot" mode -->
					<VideoVue
						v-else-if="promotedParticipantModel"
						:key="`autopilot-${promotedParticipantModel.attributes.peerId}`"
						:data-tile-session-id="promotedParticipantModel.attributes.nextcloudSessionId"
						:token="token"
						:model="promotedParticipantModel"
						:sharedData="sharedDatas[promotedParticipantModel.attributes.peerId]"
						:showTalkingHighlight="false"
						isGrid
						fitVideo
						isBig
						:isFullPage="showFullPage"
						:isSidebar="isSidebar"
						@forcePromoteVideo="forcePromotedModel = $event" />
					<!-- presenter overlay -->
					<PresenterOverlay
						v-if="shouldShowPresenterOverlay"
						:token="token"
						:model="presenterModel"
						:sharedData="presenterSharedData"
						:isLocalPresenter="showLocalScreen"
						:localMediaModel="localMediaModel"
						:isCollapsed="!showPresenterOverlay"
						@click="toggleShowPresenterOverlay" />

					<div
						v-else-if="devMode && !isGrid"
						class="dev-mode-video--promoted">
						<img :alt="placeholderName(6)" :src="placeholderImage(6)">
						<VideoBottomBar
							:hasShadow="false"
							:model="placeholderModel(6)"
							:sharedData="placeholderSharedData(6)"
							:token="token"
							:participantName="placeholderName(6)"
							isBig />
					</div>
				</div>

				<!-- Stripe or fullscreen grid depending on `isGrid` -->
				<VideosGrid
					v-if="!isSidebar"
					:isStripe="showPromotedArea"
					:isRecording="isRecording"
					:token="token"
					:isOverlap="showFullPage"
					:callParticipantModels="stripeParticipantModels"
					:screens="screens"
					:localMediaModel="localMediaModel"
					:localCallParticipantModel="localCallParticipantModel"
					:showVideoOverlay="showVideoOverlay"
					:sharedDatas="sharedDatas"
					v-bind="$attrs"
					@selectVideo="handleSelectVideo"
					@clickLocalVideo="handleClickLocalVideo" />

				<ReactionToaster
					v-if="supportedReactions?.length"
					:token="token"
					:supportedReactions="supportedReactions"
					:callParticipantModels="callParticipantModels" />

				<LiveTranscriptionRenderer
					v-if="isLiveTranscriptionEnabled"
					:token="token"
					:callParticipantModels="callParticipantModels" />

				<!-- Local video if sidebar -->
				<LocalVideo
					v-if="isSidebar && !showLocalVideo"
					ref="localVideo"
					class="local-video"
					:class="{ 'local-video--sidebar': isSidebar }"
					:fitVideo="true"
					:isStripe="true"
					:token="token"
					:localMediaModel="localMediaModel"
					:localCallParticipantModel="localCallParticipantModel"
					:isSidebar="isSidebar"
					@clickVideo="handleClickLocalVideo" />
			</div>

			<BottomBar v-if="!isRecording" :isSidebar="isSidebar" />
		</template>
	</div>
</template>

<script>
import { showMessage } from '@nextcloud/dialogs'
import { subscribe, unsubscribe } from '@nextcloud/event-bus'
import { loadState } from '@nextcloud/initial-state'
import { t } from '@nextcloud/l10n'
import debounce from 'debounce'
import { computed, provide, ref } from 'vue'
import BottomBar from './BottomBar.vue'
import SpeakersGrid from './Grid/SpeakersGrid.vue'
import VideosGrid from './Grid/VideosGrid.vue'
import EmptyCallView from './shared/EmptyCallView.vue'
import LiveTranscriptionRenderer from './shared/LiveTranscriptionRenderer.vue'
import LocalVideo from './shared/LocalVideo.vue'
import PresenterOverlay from './shared/PresenterOverlay.vue'
import ReactionToaster from './shared/ReactionToaster.vue'
import ScreenShare from './shared/ScreenShare.vue'
import VideoBottomBar from './shared/VideoBottomBar.vue'
import VideoVue from './shared/VideoVue.vue'
import ViewerOverlayCallView from './shared/ViewerOverlayCallView.vue'
import { SIMULCAST } from '../../constants.ts'
import { fetchPeers } from '../../services/callsService.ts'
import { getTalkConfig } from '../../services/CapabilitiesManager.ts'
import { EventBus } from '../../services/EventBus.ts'
import { useCallViewStore } from '../../stores/callView.ts'
import { useParticipantActivityStore } from '../../stores/participantActivity.ts'
import { useSettingsStore } from '../../stores/settings.ts'
import { callParticipantCollection, localCallParticipantModel, localMediaModel } from '../../utils/webrtc/index.js'
import RemoteVideoBlocker from '../../utils/webrtc/RemoteVideoBlocker.js'
import { placeholderImage, placeholderModel, placeholderName, placeholderSharedData } from './Grid/gridPlaceholders.ts'
import { animateTilePromotion } from './Grid/tilePromotionTransition.ts'
import { setUpSpeakerSimulation, tearDownSpeakerSimulation } from './speakerSimulation.ts'
import { useActiveSpeakers } from './useActiveSpeakers.ts'
import { useWakeLock } from './useWakeLock.ts'

const serverVersion = loadState('core', 'config', {}).version ?? '29.0.0.0'

export default {
	name: 'CallView',

	components: {
		BottomBar,
		EmptyCallView,
		VideosGrid,
		LiveTranscriptionRenderer,
		LocalVideo,
		PresenterOverlay,
		ReactionToaster,
		ScreenShare,
		SpeakersGrid,
		VideoBottomBar,
		VideoVue,
		ViewerOverlayCallView,
	},

	props: {
		token: {
			type: String,
			required: true,
		},

		// Determines whether this component is used in the sidebar
		isSidebar: {
			type: Boolean,
			default: false,
		},

		// Determines whether this component is used in the recording view
		isRecording: {
			type: Boolean,
			default: false,
		},
	},

	setup(props) {
		// Prevent the screen from turning off
		useWakeLock()

		// For debug and screenshot purposes. Set to true to enable
		const devMode = ref(false)
		provide('CallView:devModeEnabled', devMode)
		const screenshotMode = ref(false)
		provide('CallView:screenshotModeEnabled', screenshotMode)
		const settingsStore = useSettingsStore()
		// If media settings was not used, we check the global config of default devices state here
		if (!settingsStore.showMediaSettings && settingsStore.startWithoutMedia) {
			localMediaModel.disableAudio()
			localMediaModel.disableVideo()
		}

		const participantActivityStore = useParticipantActivityStore()
		const callViewStore = useCallViewStore()

		const callParticipantModels = computed(() => {
			return callParticipantCollection.callParticipantModels
				.filter((callParticipantModel) => !callParticipantModel.attributes.internal || callParticipantModel.attributes.videoAvailable)
		})

		// Several speakers share the main area only in speaker view, and only
		// when asked for. The sidebar has a layout of its own.
		const isMultiSpeaker = computed(() => {
			return callViewStore.isMultiSpeaker && !callViewStore.isGrid && !props.isSidebar
		})

		const { promotedSessionIds } = useActiveSpeakers({
			callParticipantModels,
			enabled: isMultiSpeaker,
		})

		return {
			localMediaModel,
			localCallParticipantModel,
			callParticipantCollection,
			callParticipantModels,
			devMode,
			callViewStore,
			participantActivityStore,
			isMultiSpeaker,
			promotedSessionIds,
		}
	},

	data() {
		return {
			screens: [],
			sharedDatas: {},
			raisedHandUnwatchers: {},
			speakingUnwatchers: {},
			screenUnwatchers: {},
			speakers: [],
			localSharedData: {
				screenVisible: true,
			},

			showPresenterOverlay: true,
			debounceFetchPeers: () => {},
			forcePromotedModel: null,
			// Videos controls and name
			showVideoOverlay: true,
			// Timer for the videos bottom bar
			showVideoOverlayTimer: null,
			debounceHandleMovement: () => {},
		}
	},

	computed: {
		promotedParticipantModel() {
			// Ensure at least one participant is always promoted to show in autopilot
			return this.forcePromotedModel
				// A single active speaker keeps the main area for itself, rather
				// than being the only tile of the speakers grid
				?? (this.showSpeakers ? this.promotedSpeakerModels[0] : undefined)
				// The shared data of a model is set from a watcher, so it may not
				// be there yet when this is evaluated right after the model was
				// added
				?? this.callParticipantModels.find((callParticipantModel) => this.sharedDatas[callParticipantModel.attributes.peerId]?.promoted)
				?? this.callParticipantModels[0]
		},

		// The speakers currently sharing the main area, in the order they became
		// active
		promotedSpeakerModels() {
			return this.promotedSessionIds
				.map((sessionId) => this.callParticipantModels.find((model) => model.attributes.nextcloudSessionId === sessionId))
				.filter((model) => model !== undefined)
		},

		// Whether the active speakers rule the main area. Until somebody has
		// spoken there is none, and the single promoted participant is shown as
		// usual.
		showSpeakers() {
			return this.isMultiSpeaker
				&& !this.showSelectedVideo
				&& !this.showLocalVideo
				&& !this.showLocalScreen
				&& !this.showRemoteScreen
				&& !this.showSelectedScreen
				&& this.promotedSpeakerModels.length > 0
		},

		// Whether the main area is shared between several speakers. A lone
		// speaker is shown as the promoted participant instead, so that the
		// speaker they already were is not moved around for nothing.
		showSpeakersGrid() {
			return this.showSpeakers && this.promotedSpeakerModels.length > 1
		},

		// Whether the promoted area is shown next to the stripe, rather than the
		// grid taking the whole view
		showPromotedArea() {
			return this.devMode ? !this.isGrid : (!this.isGrid || !this.callParticipantModels.length)
		},

		// Session ids of the participant tiles shown in the promoted area, be it
		// the active speakers, a selected video or the promoted participant.
		//
		// A screen share holds no participant tile: the camera of whoever shares
		// it is only shown in the stripe, so nothing is taken out of it.
		promotedAreaSessionIds() {
			if (!this.showPromotedArea
				|| this.showLocalVideo
				|| this.showLocalScreen
				|| this.showRemoteScreen
				|| this.showSelectedScreen) {
				return []
			}

			if (this.showSelectedVideo) {
				return this.selectedCallParticipantModel
					? [this.selectedCallParticipantModel.attributes.nextcloudSessionId]
					: []
			}

			if (this.showSpeakersGrid) {
				return this.promotedSpeakerModels.map((model) => model.attributes.nextcloudSessionId)
			}

			return this.promotedParticipantModel
				? [this.promotedParticipantModel.attributes.nextcloudSessionId]
				: []
		},

		// Peer ids of the speakers actually shown in the main area, which deserve
		// the same video quality as a single promoted participant
		promotedSpeakerPeerIds() {
			if (!this.showSpeakers) {
				return new Set()
			}

			return new Set(this.promotedSpeakerModels.map((model) => model.attributes.peerId))
		},

		// The tiles of the stripe. Whoever is shown in the promoted area is taken
		// out of it, as they moved from the stripe into their slot rather than
		// being shown twice.
		stripeParticipantModels() {
			if (!this.promotedAreaSessionIds.length) {
				return this.callParticipantModels
			}

			const promoted = new Set(this.promotedAreaSessionIds)
			return this.callParticipantModels.filter((model) => !promoted.has(model.attributes.nextcloudSessionId))
		},

		callParticipantModelsWithScreen() {
			return this.callParticipantModels.filter((callParticipantModel) => callParticipantModel.attributes.screen)
		},

		localScreen() {
			return localMediaModel.attributes.localScreen
		},

		screenSharingActive() {
			return this.screens.length > 0
		},

		isViewerOverlay() {
			return this.callViewStore.isViewerOverlay
		},

		isGrid() {
			return this.callViewStore.isGrid && !this.isSidebar
		},

		selectedVideoPeerId() {
			return this.callViewStore.selectedVideoPeerId
		},

		selectedCallParticipantModel() {
			if (!this.showSelectedVideo || !this.selectedVideoPeerId) {
				return null
			}
			return this.callParticipantModels.find((callParticipantModel) => {
				return callParticipantModel.attributes.peerId === this.selectedVideoPeerId
			})
		},

		hasSelectedScreen() {
			return this.selectedVideoPeerId !== null && this.screens.includes(this.selectedVideoPeerId)
		},

		hasSelectedVideo() {
			return this.selectedVideoPeerId !== null && !this.screens.includes(this.selectedVideoPeerId)
		},

		// Whether the promoted video covers the whole call view, with the stripe
		// floating over it rather than taking room of its own. A promoted area
		// held by a single participant is laid out that way, and so is one with
		// nobody in it yet.
		showFullPage() {
			return this.callParticipantModels.length <= 1
				&& !(this.showLocalScreen || this.showRemoteScreen || this.showSelectedScreen)
		},

		hasLocalVideo() {
			return this.localMediaModel.attributes.videoEnabled
		},

		hasLocalScreen() {
			return !!this.localMediaModel.attributes.localScreen
		},

		hasRemoteScreen() {
			return this.callParticipantModelsWithScreen.length > 0
		},
		// The following conditions determine what to show in the "Big container"
		// of the promoted view

		// Show selected video (other than local)
		showSelectedVideo() {
			return this.hasSelectedVideo && !this.showLocalVideo
		},

		showSelectedScreen() {
			return this.hasSelectedScreen && !this.showLocalVideo
		},

		// Shows the local video if selected
		showLocalVideo() {
			return this.hasLocalVideo && this.selectedVideoPeerId === 'local'
		},

		// Show local screen
		showLocalScreen() {
			return this.hasLocalScreen && this.selectedVideoPeerId === null && this.screens[0] === localCallParticipantModel.attributes.peerId
		},

		// Show somebody else's screen. This will show the screen of the last
		// person that shared it.
		showRemoteScreen() {
			return this.shownRemoteScreenPeerId !== null && !this.showSelectedVideo && !this.showSelectedScreen
		},

		shownRemoteScreenPeerId() {
			if (!this.screenSharingActive || !this.hasRemoteScreen) {
				return null
			}

			if (this.screens.includes(this.selectedVideoPeerId)) {
				return this.selectedVideoPeerId
			}

			if (!this.hasSelectedScreen) {
				return this.screens[0]
			}

			return null
		},

		shownRemoteScreenCallParticipantModel() {
			if (!this.shownRemoteScreenPeerId) {
				return null
			}
			return this.callParticipantModels.find((callParticipantModel) => {
				return callParticipantModel.attributes.peerId === this.shownRemoteScreenPeerId
			})
		},

		shouldShowPresenterOverlay() {
			if (this.isSidebar) {
				// Do not overload small video tile
				return false
			}

			return (this.showLocalScreen && this.hasLocalVideo)
				|| ((this.showRemoteScreen || this.showSelectedScreen)
					&& (this.shownRemoteScreenCallParticipantModel?.attributes.videoAvailable || this.isModelWithVideo(this.shownRemoteScreenCallParticipantModel)))
		},

		presenterModel() {
			// Prioritize local screen over remote screen, if both are available (as in DOM order)
			return this.showLocalScreen ? this.localCallParticipantModel : this.shownRemoteScreenCallParticipantModel
		},

		presenterSharedData() {
			return this.showLocalScreen ? this.localSharedData : this.sharedDatas[this.shownRemoteScreenPeerId]
		},

		presenterVideoBlockerEnabled() {
			return this.sharedDatas[this.shownRemoteScreenPeerId]?.remoteVideoBlocker?.isVideoEnabled()
		},

		showEmptyCallView() {
			return !this.callParticipantModels.length && !this.screenSharingActive && !this.devMode
		},

		supportedReactions() {
			return getTalkConfig(this.token, 'call', 'supported-reactions')
		},

		isLiveTranscriptionEnabled() {
			return this.callViewStore.isLiveTranscriptionEnabled
		},
	},

	watch: {
		'localCallParticipantModel.attributes.peerId': function(newValue, previousValue) {
			const index = this.screens.indexOf(previousValue)
			if (index !== -1) {
				this.screens[index] = newValue
			}
		},

		localScreen(localScreen) {
			this._setScreenAvailable(localCallParticipantModel.attributes.peerId, localScreen)
		},

		callParticipantModels(models) {
			this.updateDataFromCallParticipantModels(models)
		},

		isGrid() {
			this.adjustSimulcastQuality()
		},

		selectedVideoPeerId() {
			this.adjustSimulcastQuality()
		},

		// A speaker entering or leaving the promoted area is what promotes them,
		// so the flag is set from here rather than from `speakers`
		promotedSpeakerPeerIds() {
			this._setPromotedParticipant()
		},

		isMultiSpeaker() {
			this._setPromotedParticipant()
		},

		promotedAreaSessionIds(sessionIds, previousSessionIds) {
			// Runs before the grids are re-rendered, while the tiles are still
			// where they are about to move from
			animateTilePromotion(sessionIds, previousSessionIds ?? [])
		},

		speakers: {
			deep: true,
			handler() {
				this._setPromotedParticipant()
			},
		},

		shownRemoteScreenPeerId(value) {
			if (value) {
				this._setPromotedParticipant()
			}
		},

		screens: {
			deep: true,
			handler() {
				this._setScreenVisible()
			},
		},

		callParticipantModelsWithScreen(newValue, previousValue) {
			// Everytime a new screen is shared, switch to promoted view
			if (newValue.length > previousValue.length) {
				this.callViewStore.startPresentation(this.token)
			} else if (newValue.length === 0 && previousValue.length > 0 && !this.hasLocalScreen && !this.selectedVideoPeerId) {
				// last screen share stopped and no selected video, restoring previous state
				this.callViewStore.stopPresentation(this.token)
			}
		},

		showLocalScreen(showLocalScreen) {
			// Everytime the local screen is shared, switch to promoted view
			if (showLocalScreen) {
				this.callViewStore.startPresentation(this.token)
			} else if (this.callParticipantModelsWithScreen.length === 0 && !this.selectedVideoPeerId) {
				// last screen share stopped and no selected video, restoring previous state
				this.callViewStore.stopPresentation(this.token)
			}
		},

		hasLocalVideo(newValue) {
			if (this.selectedVideoPeerId === 'local') {
				if (!newValue) {
					this.callViewStore.setSelectedVideoPeerId(null)
				}
			}
		},

		presenterVideoBlockerEnabled(value) {
			this.showPresenterOverlay = value
		},

		showEmptyCallView: {
			immediate: true,
			handler(value) {
				this.callViewStore.setIsEmptyCallView(value)
			},
		},
	},

	created() {
		// Ensure that data is properly initialized before mounting the
		// subviews.
		this.updateDataFromCallParticipantModels(this.callParticipantModels)
	},

	mounted() {
		this.debounceFetchPeers = debounce(this.fetchPeers, 1500)
		EventBus.on('refresh-peer-list', this.debounceFetchPeers)

		this.debounceHandleMovement = debounce(this.handleMovement, 100, { immediate: true })

		callParticipantCollection.on('remove', this._lowerHandWhenParticipantLeaves)

		subscribe('switch-screen-to-id', this._switchScreenToId)

		if (OC.debug) {
			setUpSpeakerSimulation(this.token)
		}
	},

	beforeUnmount() {
		if (OC.debug) {
			tearDownSpeakerSimulation()
		}

		this.debounceFetchPeers.clear?.()
		this.debounceHandleMovement.clear?.()
		this.callViewStore.setIsEmptyCallView(true)
		EventBus.off('refresh-peer-list', this.debounceFetchPeers)

		callParticipantCollection.off('remove', this._lowerHandWhenParticipantLeaves)

		unsubscribe('switch-screen-to-id', this._switchScreenToId)

		if (this.showVideoOverlayTimer !== null) {
			clearTimeout(this.showVideoOverlayTimer)
			this.showVideoOverlayTimer = null
		}
	},

	methods: {
		t,
		// Placeholder data for devMode and screenshotMode
		placeholderImage,
		placeholderName,
		placeholderModel,
		placeholderSharedData,
		/**
		 * Updates data properties that depend on the CallParticipantModels.
		 *
		 * The data contains some properties that can not be dynamically
		 * computed but that depend on the current CallParticipantModels, so
		 * this function adds and removes elements and watchers as needed based
		 * on the given CallParticipantModels.
		 *
		 * @param {Array} models the array of CallParticipantModels
		 */
		updateDataFromCallParticipantModels(models) {
			const addedModels = models.filter((model) => !this.sharedDatas[model.attributes.peerId])
			const removedModelIds = Object.keys(this.sharedDatas).filter((sharedDataId) => models.find((model) => model.attributes.peerId === sharedDataId) === undefined)

			removedModelIds.forEach((removedModelId) => {
				this.sharedDatas[removedModelId].remoteVideoBlocker.destroy()

				delete this.sharedDatas[removedModelId]

				this.speakingUnwatchers[removedModelId]()
				// Not reactive, but not a problem
				delete this.speakingUnwatchers[removedModelId]

				this.screenUnwatchers[removedModelId]()
				// Not reactive, but not a problem
				delete this.screenUnwatchers[removedModelId]

				this.raisedHandUnwatchers[removedModelId]()
				// Not reactive, but not a problem
				delete this.raisedHandUnwatchers[removedModelId]

				const index = this.speakers.findIndex((speaker) => speaker.id === removedModelId)
				this.speakers.splice(index, 1)

				this._setScreenAvailable(removedModelId, false)
			})

			addedModels.forEach((addedModel) => {
				const sharedData = {
					promoted: false,
					remoteVideoBlocker: new RemoteVideoBlocker(addedModel),
					screenVisible: false,
				}

				this.sharedDatas[addedModel.attributes.peerId] = sharedData

				// Not reactive, but not a problem
				this.speakingUnwatchers[addedModel.attributes.peerId] = this.$watch(function() {
					return addedModel.attributes.speaking
				}, function(speaking) {
					this._setSpeaking(addedModel.attributes.peerId, speaking)
				})

				this.speakers.push({
					id: addedModel.attributes.peerId,
					active: false,
				})

				// Not reactive, but not a problem
				this.screenUnwatchers[addedModel.attributes.peerId] = this.$watch(function() {
					return addedModel.attributes.screen
				}, function(screen) {
					this._setScreenAvailable(addedModel.attributes.peerId, screen)
				})

				// Not reactive, but not a problem
				this.raisedHandUnwatchers[addedModel.attributes.peerId] = this.$watch(function() {
					return addedModel.attributes.raisedHand
				}, function(raisedHand) {
					this._handleParticipantRaisedHand(addedModel, raisedHand)
				})

				this.adjustSimulcastQualityForParticipant(addedModel)
			})
		},

		_setSpeaking(peerId, speaking) {
			if (speaking) {
				// Move the speaker to the first element of the list
				const index = this.speakers.findIndex((speaker) => speaker.id === peerId)
				const speaker = this.speakers[index]
				speaker.active = true
				this.speakers.splice(index, 1)
				this.speakers.unshift(speaker)

				return
			}

			// Set the speaker as not speaking
			const index = this.speakers.findIndex((speaker) => speaker.id === peerId)
			const speaker = this.speakers[index]
			speaker.active = false

			// Move the speaker after all the active speakers
			if (index === 0) {
				this.speakers.shift()

				const firstInactiveSpeakerIndex = this.speakers.findIndex((speaker) => !speaker.active)
				if (firstInactiveSpeakerIndex === -1) {
					this.speakers.push(speaker)
				} else {
					this.speakers.splice(firstInactiveSpeakerIndex, 0, speaker)
				}
			}
		},

		_handleParticipantRaisedHand(callParticipantModel, raisedHand) {
			const nickName = callParticipantModel.attributes.name || callParticipantModel.attributes.userId
			// sometimes the nick name is not available yet...
			if (nickName) {
				if (raisedHand?.state) {
					showMessage(t('spreed', '{nickName} raised their hand.', { nickName }))
				}
			} else {
				if (raisedHand?.state) {
					showMessage(t('spreed', 'A participant raised their hand.'))
				}
			}

			this.participantActivityStore.setParticipantHandRaised({
				sessionId: callParticipantModel.attributes.nextcloudSessionId,
				raisedHand,
			})
		},

		_lowerHandWhenParticipantLeaves(callParticipantCollection, callParticipantModel) {
			this.participantActivityStore.setParticipantHandRaised({
				sessionId: callParticipantModel.attributes.nextcloudSessionId,
				raisedHand: false,
			})
		},

		_setScreenAvailable(id, screen) {
			if (screen) {
				this.screens.unshift(id)

				return
			}

			const index = this.screens.indexOf(id)
			if (index !== -1) {
				this.screens.splice(index, 1)
			}
		},

		_setPromotedParticipant() {
			let promotedPeerIds = []

			if (this.screenSharingActive) {
				if (this.shownRemoteScreenPeerId) {
					promotedPeerIds = [this.shownRemoteScreenPeerId]
				}
			} else if (this.isMultiSpeaker) {
				// The promoted flag follows the promoted area rather than whoever
				// speaks: a speaker only moves up once they spoke long enough, and
				// promoting them as soon as they speak would mark a tile that is
				// still in the stripe as promoted.
				promotedPeerIds = this.promotedSpeakerModels.map((model) => model.attributes.peerId)
			} else if (this.speakers.length) {
				promotedPeerIds = [this.speakers[0].id]
			}

			promotedPeerIds = promotedPeerIds.filter((peerId) => this.sharedDatas[peerId])

			// Ensure at least one participant is always promoted to show in autopilot
			if (promotedPeerIds.length) {
				Object.keys(this.sharedDatas).forEach((peerId) => {
					this.sharedDatas[peerId].promoted = promotedPeerIds.includes(peerId)
				})
			}

			this.adjustSimulcastQuality()
		},

		_switchScreenToId(id) {
			const index = this.screens.indexOf(id)
			if (index === -1) {
				return
			}

			if (this.callViewStore.presentationStarted) {
				this.callViewStore.setCallViewMode({
					token: this.token,
					isGrid: false,
					isStripeOpen: false,
					clearLast: false,
				})
			} else {
				this.callViewStore.startPresentation(this.token)
			}
			this.callViewStore.setSelectedVideoPeerId(null)
			this.screens.splice(index, 1)
			this.screens.unshift(id)
		},

		_setScreenVisible() {
			this.localSharedData.screenVisible = false

			Object.values(this.sharedDatas).forEach((sharedData) => {
				sharedData.screenVisible = false
			})

			if (!this.screens.length) {
				return
			}

			if (this.screens[0] === this.localCallParticipantModel.attributes.peerId) {
				this.localSharedData.screenVisible = true

				return
			}

			this.sharedDatas[this.screens[0]].screenVisible = true
		},

		handleSelectVideo(peerId) {
			if (this.isSidebar) {
				return
			}
			this.callViewStore.setSelectedVideoPeerId(peerId)
			this.callViewStore.setCallViewMode({
				token: this.token,
				isGrid: false,
				isStripeOpen: false,
				clearLast: false,
			})
		},

		handleClickLocalVideo() {
			// DO nothing if no video
			if (!this.hasLocalVideo || this.isSidebar) {
				return
			}
			// Deselect possible selected video
			this.callViewStore.setSelectedVideoPeerId('local')
			this.callViewStore.setCallViewMode({
				token: this.token,
				isGrid: false,
				isStripeOpen: false,
				clearLast: false,
			})
		},

		async fetchPeers() {
			// The recording participant does not have a Nextcloud session, so
			// it can not fetch the peers. This should not be a problem, as all
			// the needed data for the recording should be (eventually)
			// available in the signaling data.
			if (this.isRecording) {
				return
			}

			const token = this.token
			try {
				const response = await fetchPeers(token)
				this.$store.dispatch('purgePeersStore')

				response.data.ocs.data.forEach((peer) => {
					this.$store.dispatch('addPeer', {
						token,
						peer,
					})
				})
			} catch (exception) {
				// Just means guests have no name, so don't error …
				console.error(exception)
			}
		},

		adjustSimulcastQuality() {
			this.callParticipantModels.forEach((callParticipantModel) => {
				this.adjustSimulcastQualityForParticipant(callParticipantModel)
			})
		},

		adjustSimulcastQualityForParticipant(callParticipantModel) {
			// Always use the high temporal layer, as a low frame rate can look
			// bad specially with a low number of participants.
			if (this.isGrid) {
				callParticipantModel.setSimulcastVideoQuality(SIMULCAST.MEDIUM, SIMULCAST.HIGH)
			} else if (this.sharedDatas[callParticipantModel.attributes.peerId]?.promoted
				|| this.selectedVideoPeerId === callParticipantModel.attributes.peerId
				|| this.promotedSpeakerPeerIds.has(callParticipantModel.attributes.peerId)) {
				callParticipantModel.setSimulcastVideoQuality(SIMULCAST.HIGH, SIMULCAST.HIGH)
			} else {
				callParticipantModel.setSimulcastVideoQuality(SIMULCAST.LOW, SIMULCAST.HIGH)
			}
		},

		isModelWithVideo(callParticipantModel) {
			if (!callParticipantModel) {
				return false
			}
			return callParticipantModel.attributes.videoAvailable
				&& this.sharedDatas[callParticipantModel.attributes.peerId].remoteVideoBlocker.isVideoEnabled()
				&& (typeof callParticipantModel.attributes.stream === 'object')
		},

		toggleShowPresenterOverlay() {
			if (!this.showLocalScreen && !this.presenterVideoBlockerEnabled) {
				this.sharedDatas[this.shownRemoteScreenPeerId].remoteVideoBlocker.setVideoEnabled(true)
			} else {
				this.showPresenterOverlay = !this.showPresenterOverlay
			}
		},

		handleMovement() {
			if (this.showVideoOverlayTimer !== null) {
				clearTimeout(this.showVideoOverlayTimer)
			}
			this.showVideoOverlay = true
			this.showVideoOverlayTimer = setTimeout(() => {
				this.showVideoOverlay = false
				this.showVideoOverlayTimer = null
			}, 5_000)
		},

		hideOverlay() {
			if (this.showVideoOverlayTimer !== null) {
				clearTimeout(this.showVideoOverlayTimer)
				this.showVideoOverlayTimer = null
			}
			this.showVideoOverlay = false
		},
	},
}
</script>

<style lang="scss" scoped>
@use '../../assets/variables.scss' as *;

#call-container {
	width: 100%;
	height: 100%;
	background-color: $color-call-background;
	--grid-gap: calc(var(--default-grid-baseline) * 2);
	--top-bar-height: 51px;
	--wrapper-padding: calc(var(--default-grid-baseline) * 2.5);
	--bottom-bar-height: calc(var(--default-clickable-area) + var(--wrapper-padding) * 2);
	// For sidebar integrations: show container in a 16/9 proportion (+ top/bottom bar) based on the sidebar width
	--sidebar-container-height: calc(56.25% + var(--top-bar-height) + var(--bottom-bar-height));
}

#videos {
	position: absolute;
	width: 100%;
	height: calc(100% - (var(--top-bar-height) + var(--bottom-bar-height)));
	top: var(--top-bar-height);
	overflow: hidden;
	display: flex;
	justify-content: space-around;
	align-items: flex-end;
	flex-direction: column;
	padding-inline: var(--wrapper-padding);

	&.is-sidebar {
		width: 100%;
		height: 100%;
		inset: 0;
		padding: 0;

		:deep(.video-container-big) {
			width: 100%;
			height: 100%;
			padding: 0;
			border-radius: 0;
		}
	}
}

.video__promoted {
	position: relative;
	height: 100%;
	width: 100%;

	&.full-page {
		// force the promoted remote or local video to cover the whole call view
		// doesn't affect screen shares, as it's a different MediaStream
		position: static;
	}

	.dev-mode-video--promoted {
		position: absolute;
		width: 100%;
		height: 100%;
		display: flex;
		justify-content: center;
	}

	.dev-mode-video--promoted img {
		position: absolute;
		height: 100%;
		aspect-ratio: 4 / 3;
		object-fit: cover;
		border-radius: var(--border-radius-element, calc(var(--default-clickable-area) / 2));
	}
}

.local-video {
	position: absolute;
	inset-inline-end: 0;
	bottom: 0;
	width: fit-content;
	max-width: 300px;
	max-height: 250px;

	&--sidebar {
		max-width: 150px;
		max-height: 100px;
		bottom: var(--bottom-bar-height);
		margin: var(--default-grid-baseline);
	}
}

#videos.hidden {
	display: none;
}

:deep(video) {
	z-index: 0;
	/* default filter for slightly better look */
	/* Disabled for now as it causes a huuuuge performance drop.
	 CPU usage is more than halved without this.
	 -webkit-filter: contrast(1.1) saturate(1.1) sepia(.1);
	 filter: contrast(1.1) saturate(1.1) sepia(.1);
	 */
	vertical-align: top; /* fix white line below video */
}

#videos :deep(video) {
	padding: 0;
}

</style>
